import { FC, useCallback, useEffect, useReducer, useRef } from 'react'

import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import { ReactNodeViewRenderer, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import lowlight from 'lowlight'
import { Helmet } from 'react-helmet'
import 'highlight.js/styles/atom-one-dark-reasonable.css'

import { SearchEntityType } from '@tribeplatform/gql-client/types'
import { useEmbed, useSearch } from '@tribeplatform/react-sdk/hooks'

import {
  DialogsAction,
  DialogsState,
  ReducerActionKind,
  TiptapContextType,
  TiptapProviderType,
} from './@types'
import {
  AttachmentPickerButton,
  AttachmentPickerButtonRef,
} from './components/AttachmentPickerButton'
import { CodeBlockComponent } from './components/CodeBlockComponent'
import { Embed as EmbedExtension } from './extensions/embed'
import { Emoji } from './extensions/emoji'
import { CustomHeading } from './extensions/heading'
import { AsyncImage } from './extensions/image'
import { CustomLink } from './extensions/link'
import { Mention } from './extensions/mention'
import { mention } from './extensions/mention/options'
import { SlashMenu } from './extensions/slash-menu'
import { slashMenuCommand } from './extensions/slash-menu/click-handler'
import { MenuTypes } from './extensions/slash-menu/items'
import { Video } from './extensions/video'
import { useHandleFiles } from './hooks/useHandleFiles'
import { TiptapContext } from './TiptapContext'

import './styles/prosemirror.css'

const initialDialogsState: DialogsState = {
  link: false,
  embed: false,
  video: false,
  audio: false,
  image: false,
  giphy: false,
  embedItem: undefined,
  textFormatMenu: false,
}

function reducer(state: DialogsState, action: DialogsAction) {
  switch (action.type) {
    case ReducerActionKind.SET:
      return { ...state, [action.key]: action.value }
    case ReducerActionKind.SET_EMBED_ITEM:
      return { ...state, embedItem: action.embedItem }
    default:
      throw state
  }
}

export const TiptapProvider: FC<TiptapProviderType> = ({
  children,
  content,
  embeds,
  mentions,
  // TODO: replace this with an extension
  handleAttachmentSelect,
}) => {
  const { asyncQuery: embed } = useEmbed()
  const { asyncQuery: search } = useSearch({ fields: 'all' })
  const isTouched = useRef<boolean>(false)
  const attachmentControlRef = useRef<AttachmentPickerButtonRef>()
  const [state, dispatch] = useReducer(reducer, initialDialogsState)

  const toggleDialog: TiptapContextType['toggleDialog'] = useCallback(
    (key, value) => {
      dispatch({ type: ReducerActionKind.SET, key, value })
    },
    [],
  )

  const onSlashMenuItemClick: TiptapContextType['onSlashMenuItemClick'] =
    useCallback(
      ({ editor, props, range }) => {
        let _range = range
        if (!_range) {
          _range = {
            from: editor.view.state.selection.from,
            to: editor.view.state.selection.to,
          }
        }
        slashMenuCommand({ props, editor, range: _range })

        switch (props.type) {
          case MenuTypes.Giphy:
            toggleDialog('giphy', true)
            break
          case MenuTypes.Video:
            toggleDialog('video', true)
            break
          case MenuTypes.Audio:
            toggleDialog('audio', true)
            break
          case MenuTypes.Embed:
            toggleDialog('embed', true)
            dispatch({
              type: ReducerActionKind.SET_EMBED_ITEM,
              embedItem: props,
            })
            break
          case MenuTypes.Link:
            toggleDialog('link', true)
            break
          case MenuTypes.Attachment:
            attachmentControlRef.current.open()
            break
          case MenuTypes.Image:
            toggleDialog('image', true)
            break
          default:
        }
      },
      [toggleDialog],
    )

  const editor = useEditor({
    content,
    extensions: [
      StarterKit.configure({
        heading: false,
      }),
      CustomHeading.configure({
        levels: [2, 3],
      }),
      Underline,
      EmbedExtension.configure({
        embeds,
      }),
      AsyncImage,
      Placeholder.configure({
        placeholder: `What are your thoughts? (Type ' / ' to add images, files, or links)`,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'text-actionPrimary-600 hover:text-actionPrimary-500',
        },
        mentions,
        suggestion: mention({
          search: ({ query }) => {
            if (!query) return new Promise(res => res(undefined))

            return search({
              input: { query },
            }).then(result => {
              const members = result?.hits.find(
                hit => hit.entityType === SearchEntityType.MEMBER,
              )

              return members?.hits?.map(m => ({
                id: m.entityId,
                title: m.title,
                icon: (m?.media as any)?.url,
              }))
            })
          },
        }),
      }),
      CustomLink.configure({
        openOnClick: false,
        onPaste: async (m, editor) => {
          const { from } = editor.state.selection
          const href = m?.[0]?.data?.href
          const text = m?.[0]?.text
          const oembed = await embed({ url: href })

          if (oembed.html) {
            editor
              .chain()
              .deleteRange({ from, to: from + text.length })
              .setEmbed({ embed: oembed })
              .setTextSelection(from + 1)
              .insertContent(' ')
              .run()
          }
        },
        HTMLAttributes: {
          class: 'text-actionAccent-600 hover:text-actionAccentHover-500',
        },
      }),
      Emoji,
      Video,
      SlashMenu.configure({
        command: onSlashMenuItemClick,
      }),
      CodeBlockLowlight.extend({
        addNodeView: () => ReactNodeViewRenderer(CodeBlockComponent),
      }).configure({
        lowlight,
        defaultLanguage: 'javascript',
      }),
    ],
  })

  const { onFileUpload, loading } = useHandleFiles({
    editor,
    handleAttachmentSelect,
  })

  useEffect(() => {
    const onUpdate = () => {
      isTouched.current = true
    }

    if (editor) {
      editor.on('update', onUpdate)

      return () => {
        editor.off('update', onUpdate)
      }
    }
  }, [editor])

  return (
    <>
      {/* We will load this conditionally soon. Have to take into account a lot of lifecycle methods. */}
      <Helmet>
        <script src="//cdn.iframe.ly/embed.js" async></script>
      </Helmet>
      <TiptapContext.Provider
        value={{
          withProvider: true,
          isTouched: isTouched.current,
          editor,
          onFileUpload,
          onSlashMenuItemClick,
          toggleDialog,
          state: {
            ...state,
            loading,
          },
        }}
      >
        {children}

        <span className="hidden">
          <AttachmentPickerButton
            ref={attachmentControlRef}
            onAttachmentSelect={handleAttachmentSelect}
          />
        </span>
      </TiptapContext.Provider>
    </>
  )
}

TiptapProvider.displayName = 'TiptapProvider'
